import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Minus, Plus, X, ShoppingBag, ArrowRight } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../lib/utils';

export default function CartPage() {
  const { t } = useTranslation();
  const { cart, updateItem, removeItem, loading } = useCart();

  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  if (loading) {
    return (
      <StoreLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-muted rounded w-1/3" />
            <div className="h-32 bg-muted rounded" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl font-bold mb-8"
          >
            {t('cart.title')}
          </motion.h1>

          {isEmpty ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <ShoppingBag className="h-20 w-20 text-muted-foreground/50 mx-auto mb-6" />
              <p className="text-muted-foreground text-xl mb-8">{t('cart.empty')}</p>
              <Button size="lg" asChild>
                <Link to="/looks">
                  {t('cart.continue')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-4 p-4 border border-border rounded-lg"
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Image */}
                    <div className="w-24 h-32 bg-muted overflow-hidden flex-shrink-0">
                      <img
                        src={
                          item.look?.images?.[0] ||
                          item.product?.images?.[0] ||
                          'https://images.unsplash.com/photo-1652281846260-14c1bdd5e9a0?w=200'
                        }
                        alt={item.look?.name || item.product?.name || 'Item'}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-lg truncate">
                        {item.look?.name || item.product?.name || 'Item'}
                      </h3>
                      {item.look && (
                        <p className="text-sm text-muted-foreground">
                          {item.look.items?.length || 0} articles inclus
                        </p>
                      )}
                      <p className="text-lg font-semibold mt-2">
                        {formatPrice(item.price_cents)}
                      </p>

                      {/* Quantity */}
                      <div className="flex items-center gap-3 mt-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateItem(item.id, Math.max(1, item.quantity - 1))}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateItem(item.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Remove */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                ))}
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="border border-border rounded-lg p-6 sticky top-24"
                >
                  <h3 className="font-semibold text-lg mb-4">Récapitulatif</h3>

                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('cart.subtotal')}</span>
                      <span>{formatPrice(cart.subtotal_cents)}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('cart.shipping')}</span>
                      <span>
                        {cart.shipping_cents === 0 
                          ? t('cart.shipping.free')
                          : formatPrice(cart.shipping_cents)
                        }
                      </span>
                    </div>

                    {cart.discount_cents > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>{t('cart.discount')}</span>
                        <span>-{formatPrice(cart.discount_cents)}</span>
                      </div>
                    )}

                    <div className="border-t border-border pt-3 mt-3">
                      <div className="flex justify-between text-lg font-semibold">
                        <span>{t('cart.total')}</span>
                        <span>{formatPrice(cart.total_cents)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Promo code */}
                  <div className="mt-6">
                    <label className="text-sm text-muted-foreground">{t('cart.promo')}</label>
                    <div className="flex gap-2 mt-1">
                      <Input placeholder="CODE" className="flex-1" />
                      <Button variant="outline">{t('cart.promo.apply')}</Button>
                    </div>
                  </div>

                  <Button 
                    size="lg" 
                    className="w-full mt-6"
                    asChild
                    data-testid="proceed-to-checkout"
                  >
                    <Link to="/checkout">
                      {t('cart.checkout')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>

                  <Button 
                    variant="link" 
                    className="w-full mt-2"
                    asChild
                  >
                    <Link to="/looks">{t('cart.continue')}</Link>
                  </Button>
                </motion.div>
              </div>
            </div>
          )}
        </div>
      </section>
    </StoreLayout>
  );
}
